import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import FirstLoginSetup from "@/pages/FirstLoginSetup";
import ShopFirstLoginSetup from "@/pages/ShopFirstLoginSetup";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AffiliateDashboard from "@/pages/AffiliateDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import ShopDashboard from "@/pages/ShopDashboard";

function needsSetup(user) {
  return user && (user.role === "customer" || user.role === "affiliate") && !user.setup_completed_at;
}

function shopNeedsSetup(shop) {
  return shop && !shop.setup_completed_at;
}

function RoleGate({ role, children }) {
  const { user, shop, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (role === "shop") {
    if (!shop) return <Navigate to="/" replace />;
    if (shopNeedsSetup(shop)) return <ShopFirstLoginSetup />;
    return children;
  }
  if (!user) return <Navigate to="/" replace />;
  if (needsSetup(user)) return <FirstLoginSetup />;
  if (role && user.role !== role) return <Navigate to={`/${user.role}`} replace />;
  return children;
}

function Root() {
  const { user, shop, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;
  if (shop) {
    if (shopNeedsSetup(shop)) return <ShopFirstLoginSetup />;
    return <Navigate to="/shop" replace />;
  }
  if (user) {
    if (needsSetup(user)) return <FirstLoginSetup />;
    return <Navigate to={`/${user.role}`} replace />;
  }
  return <Login />;
}

export default function App() {
  return (
    <div className="App noise-bg">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Root />} />
            <Route path="/customer" element={<RoleGate role="customer"><CustomerDashboard /></RoleGate>} />
            <Route path="/affiliate" element={<RoleGate role="affiliate"><AffiliateDashboard /></RoleGate>} />
            <Route path="/admin" element={<RoleGate role="admin"><AdminDashboard /></RoleGate>} />
            <Route path="/shop" element={<RoleGate role="shop"><ShopDashboard /></RoleGate>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster theme="dark" richColors position="top-right" />
      </AuthProvider>
    </div>
  );
}
